/**
 * AgregarLugarModal.js - Modal para sugerir un lugar nuevo
 *
 * Permite a usuarios sugerir negocios que conocen.
 * Los lugares quedan como "pendiente" hasta que un admin los verifique.
 */
import React, { useState, useMemo } from 'react';
import { createPlace } from '../services/placesService';
import { addFavorite } from '../services/favoritesService';

// Categorías por tipo
const categoriasEstablecimiento = [
  { id: 'Veterinaria', nombre: 'Veterinaria' },
  { id: 'Farmacia', nombre: 'Farmacia' },
  { id: 'Parques', nombre: 'Parques' },
  { id: 'Pensión', nombre: 'Pensión' },
  { id: 'Estilistas', nombre: 'Estilistas' },
  { id: 'Cerrajería', nombre: 'Cerrajería' },
  { id: 'Farmacia 24h', nombre: 'Farmacia 24h' },
  { id: 'Conveniencia', nombre: 'Conveniencia' }
];

const categoriasProveedor = [
  { id: 'Paseadores', nombre: 'Paseadores' },
  { id: 'Entrenadores', nombre: 'Entrenadores' },
  { id: 'Cuidadores', nombre: 'Cuidadores' },
  { id: 'Estilistas', nombre: 'Estilistas' }
];

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0',
    color: '#6c757d'
  },
  body: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid #e9ecef',
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    backgroundColor: '#fff'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    fontSize: '16px',
    minHeight: '80px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  radioGroup: {
    display: 'flex',
    gap: '16px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '12px 16px',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    flex: 1,
    transition: 'all 0.2s ease'
  },
  radioLabelSelected: {
    borderColor: '#0d6efd',
    backgroundColor: '#e7f1ff'
  },
  helpText: {
    fontSize: '12px',
    color: '#6c757d',
    marginTop: '4px'
  },
  successMessage: {
    textAlign: 'center',
    padding: '20px'
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  }
};

export default function AgregarLugarModal({
  isOpen,
  onClose,
  perfilId = null,
  perfilTipo = null
}) {
  const [step, setStep] = useState('form'); // 'form', 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdPlace, setCreatedPlace] = useState(null);

  const [formData, setFormData] = useState({
    tipo: 'establecimiento',
    categoria: '',
    nombre: '',
    telefono: '',
    direccion: '',
    zonas: '',
    notas: ''
  });

  const categorias = useMemo(() => {
    return formData.tipo === 'establecimiento'
      ? categoriasEstablecimiento
      : categoriasProveedor;
  }, [formData.tipo]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Reset categoria cuando cambia el tipo
      if (field === 'tipo') {
        updated.categoria = '';
      }
      return updated;
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }
    if (!formData.categoria) {
      setError('La categoría es requerida');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        tipo: formData.tipo,
        categoria: formData.categoria,
        nombre: formData.nombre.trim(),
        telefono: formData.telefono.trim() || null,
        direccion: formData.tipo === 'establecimiento' ? formData.direccion.trim() || null : null,
        zonas: formData.tipo === 'proveedor' && formData.zonas.trim()
          ? formData.zonas.split(',').map(z => z.trim())
          : [],
        notasInternas: formData.notas.trim() || null
      };

      const lugar = await createPlace(data);
      setCreatedPlace(lugar);
      setStep('success');

    } catch (err) {
      console.error('Error creando lugar:', err);
      setError(err.message || 'Error al enviar sugerencia');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToFavorites = async () => {
    if (!createdPlace) return;

    setLoading(true);
    try {
      await addFavorite(createdPlace.id, perfilId || 'general', perfilTipo || 'general');
      onClose(true); // true indica que se agregó a favoritos
    } catch (err) {
      console.error('Error agregando a favoritos:', err);
      // Aún así cerrar el modal
      onClose(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    onClose(false);
  };

  const resetForm = () => {
    setStep('form');
    setFormData({
      tipo: 'establecimiento',
      categoria: '',
      nombre: '',
      telefono: '',
      direccion: '',
      zonas: '',
      notas: ''
    });
    setError(null);
    setCreatedPlace(null);
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={() => onClose(false)}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h5 style={modalStyles.title}>
            {step === 'form' ? 'Agregar un lugar que conozco' : 'Gracias por tu sugerencia'}
          </h5>
          <button
            style={modalStyles.closeButton}
            onClick={() => onClose(false)}
            disabled={loading}
          >
            &times;
          </button>
        </div>

        {step === 'form' && (
          <form onSubmit={handleSubmit}>
            <div style={modalStyles.body}>
              {/* Tipo de servicio */}
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Tipo de servicio</label>
                <div style={modalStyles.radioGroup}>
                  <label
                    style={{
                      ...modalStyles.radioLabel,
                      ...(formData.tipo === 'establecimiento' ? modalStyles.radioLabelSelected : {})
                    }}
                  >
                    <input
                      type="radio"
                      name="tipo"
                      value="establecimiento"
                      checked={formData.tipo === 'establecimiento'}
                      onChange={(e) => handleChange('tipo', e.target.value)}
                    />
                    <div>
                      <div style={{ fontWeight: '500' }}>Establecimiento</div>
                      <small style={{ color: '#6c757d' }}>Local, consultorio</small>
                    </div>
                  </label>
                  <label
                    style={{
                      ...modalStyles.radioLabel,
                      ...(formData.tipo === 'proveedor' ? modalStyles.radioLabelSelected : {})
                    }}
                  >
                    <input
                      type="radio"
                      name="tipo"
                      value="proveedor"
                      checked={formData.tipo === 'proveedor'}
                      onChange={(e) => handleChange('tipo', e.target.value)}
                    />
                    <div>
                      <div style={{ fontWeight: '500' }}>Proveedor</div>
                      <small style={{ color: '#6c757d' }}>A domicilio</small>
                    </div>
                  </label>
                </div>
              </div>

              {/* Categoría */}
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>
                  Categoría <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  style={modalStyles.select}
                  value={formData.categoria}
                  onChange={(e) => handleChange('categoria', e.target.value)}
                  required
                >
                  <option value="">Seleccionar...</option>
                  {categorias.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Nombre */}
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>
                  Nombre <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  style={modalStyles.input}
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  placeholder="Ej: Veterinaria San José"
                  required
                />
              </div>

              {/* Teléfono */}
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Teléfono (opcional)</label>
                <input
                  type="tel"
                  style={modalStyles.input}
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  placeholder="Ej: 2281234567"
                />
              </div>

              {/* Dirección (solo establecimiento) */}
              {formData.tipo === 'establecimiento' && (
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Dirección (opcional)</label>
                  <input
                    type="text"
                    style={modalStyles.input}
                    value={formData.direccion}
                    onChange={(e) => handleChange('direccion', e.target.value)}
                    placeholder="Ej: Av. Ávila Camacho 123, Centro"
                  />
                </div>
              )}

              {/* Zonas (solo proveedor) */}
              {formData.tipo === 'proveedor' && (
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Zonas donde trabaja (opcional)</label>
                  <input
                    type="text"
                    style={modalStyles.input}
                    value={formData.zonas}
                    onChange={(e) => handleChange('zonas', e.target.value)}
                    placeholder="Ej: Centro, Ánimas, Jardines"
                  />
                  <div style={modalStyles.helpText}>Separa las zonas con comas</div>
                </div>
              )}

              {/* Notas */}
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Notas adicionales (opcional)</label>
                <textarea
                  style={modalStyles.textarea}
                  value={formData.notas}
                  onChange={(e) => handleChange('notas', e.target.value)}
                  placeholder="Cualquier información útil..."
                />
              </div>

              {error && (
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#f8d7da',
                  color: '#842029',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}
            </div>

            <div style={modalStyles.footer}>
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ced4da',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#0d6efd',
                  color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Enviando...' : 'Enviar sugerencia'}
              </button>
            </div>
          </form>
        )}

        {step === 'success' && (
          <>
            <div style={modalStyles.body}>
              <div style={modalStyles.successMessage}>
                <div style={modalStyles.successIcon}>✓</div>
                <h6 style={{ marginBottom: '8px' }}>
                  ¡Sugerencia enviada!
                </h6>
                <p style={{ color: '#6c757d', marginBottom: '16px' }}>
                  Tu sugerencia será revisada por nuestro equipo.
                  Una vez verificada, aparecerá en el directorio.
                </p>
                <p style={{ fontSize: '14px', color: '#6c757d' }}>
                  ¿Deseas agregar "{createdPlace?.nombre}" a tus favoritos?
                </p>
              </div>
            </div>

            <div style={modalStyles.footer}>
              <button
                type="button"
                onClick={handleCloseSuccess}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ced4da',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                No, gracias
              </button>
              <button
                type="button"
                onClick={handleAddToFavorites}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#198754',
                  color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Agregando...' : 'Sí, agregar a favoritos'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
